/**
 * Celebrates the moment a cart crosses the free shipping threshold.
 *
 * The bar element itself is never replaced on a cart update -- theme.mergeNodes only
 * copies its attributes across -- so watching data-free-shipping-progress is enough to
 * catch the crossing, and the element survives to run the animation.
 */
(function freeShippingBarConfetti() {
  // The snippet renders in both the cart drawer and the cart page, so this file can be
  // requested twice on one page. Defining the element twice throws.
  if (window.customElements.get('free-shipping-bar')) return;

  var COLORS = ['#ff6b6b', '#feca57', '#48dbfb', '#1dd1a1', '#f368e0', '#ff9ff3'];
  var PARTICLE_COUNT = 70;
  var DURATION = 1600;
  // The drawer slides in over 0.34s. Wait for it to settle before deciding whether this
  // copy of the bar is the one the shopper can actually see.
  var SETTLE_DELAY = 400;
  var CANVAS_HEADROOM = 100;

  var FreeShippingBar = class extends HTMLElement {
    connectedCallback() {
      this.thresholdMet = this.dataset.freeShippingProgress === '1';
      this.observer = new MutationObserver(this.handleProgressChange.bind(this));
      this.observer.observe(this, {
        attributes: true,
        attributeFilter: ['data-free-shipping-progress']
      });
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      this.stopConfetti();
    }

    handleProgressChange() {
      var met = this.dataset.freeShippingProgress === '1';
      // Only fire on the crossing. A cart that was already over the line re-renders on
      // every quantity change, and celebrating each time would be noise.
      if (met && !this.thresholdMet) this.celebrate();
      this.thresholdMet = met;
    }

    celebrate() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      clearTimeout(this.celebrateTimer);
      this.celebrateTimer = setTimeout(this.burst.bind(this), SETTLE_DELAY);
    }

    isOnScreen() {
      var drawer = this.closest('.drawer');
      if (drawer && !drawer.hasAttribute('open')) return false;

      var rect = this.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return false;
      return rect.bottom > 0
        && rect.top < window.innerHeight
        && rect.right > 0
        && rect.left < window.innerWidth;
    }

    burst() {
      if (!this.isOnScreen()) return;
      this.stopConfetti();

      var rect = this.getBoundingClientRect();
      var width = rect.width;
      var height = rect.height + CANVAS_HEADROOM;
      var dpr = Math.min(window.devicePixelRatio || 1, 2);

      var canvas = document.createElement('canvas');
      canvas.className = 'free-shipping-bar__confetti';
      canvas.setAttribute('aria-hidden', 'true');
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.height = height + 'px';
      this.appendChild(canvas);

      var ctx = canvas.getContext('2d');
      if (!ctx) {
        this.stopConfetti();
        return;
      }
      ctx.scale(dpr, dpr);

      // Launched from along the bar, which sits at the bottom edge of the canvas.
      var particles = [];
      for (var i = 0; i < PARTICLE_COUNT; i += 1) {
        particles.push({
          x: Math.random() * width,
          y: height,
          vx: (Math.random() - 0.5) * 5,
          vy: -(6 + Math.random() * 6),
          size: 4 + Math.random() * 4,
          color: COLORS[i % COLORS.length],
          angle: Math.random() * Math.PI,
          spin: (Math.random() - 0.5) * 0.3
        });
      }

      var start = performance.now();
      var step = function (now) {
        var elapsed = now - start;
        if (elapsed >= DURATION) {
          this.stopConfetti();
          return;
        }

        ctx.clearRect(0, 0, width, height);
        ctx.globalAlpha = 1 - elapsed / DURATION;

        for (var j = 0; j < particles.length; j += 1) {
          var p = particles[j];
          p.x += p.vx;
          p.y += p.vy;
          p.vy += 0.28;
          p.vx *= 0.99;
          p.angle += p.spin;

          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.angle);
          ctx.fillStyle = p.color;
          ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
          ctx.restore();
        }

        this.confettiFrame = window.requestAnimationFrame(step);
      }.bind(this);

      this.confettiFrame = window.requestAnimationFrame(step);
    }

    stopConfetti() {
      if (this.confettiFrame) {
        window.cancelAnimationFrame(this.confettiFrame);
        this.confettiFrame = null;
      }
      var canvas = this.querySelector('.free-shipping-bar__confetti');
      if (canvas) canvas.remove();
    }
  };

  window.customElements.define('free-shipping-bar', FreeShippingBar);
})();
